import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { XMarkIcon, PlusIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, PhotoIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ServicePackage, CreateServicePackageRequest } from '../../types/service';
import { Portfolio, PortfolioFolder } from '../../types';
import Button from '../common/Button';
import { createPortal } from 'react-dom';
import { userAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { resolveImageUrl } from '../../utils/image';

interface ManagePackageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateServicePackageRequest) => Promise<void>;
  initialData?: ServicePackage;
  loading?: boolean;
}

interface ArrayField { value: string }

interface FormValues {
  title: string;
  description: string;
  price: number;
  currency: string;
  category: string;
  session_duration_hours: number;
  number_of_days: number;
  number_of_looks: number;
  shoot_type: string;
  team_size: number;
  locations: ArrayField[];
  travel_included: boolean;
  max_travel_km: number;
  delivery_time_days: number;
  number_of_edited_photos: number;
  number_of_raw_photos: number;
  video_included: boolean;
  video_duration_seconds: number;
  delivery_format: string;
  revisions: number;
  inclusions: ArrayField[];
  perks: ArrayField[];
  exclusions: ArrayField[];
  advance_percentage: number;
  requires_consultation: boolean;
}

const inputClass = "w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-black/5 dark:focus:ring-white/5 focus:border-black dark:focus:border-white transition-all text-sm text-gray-900 dark:text-white";
const labelClass = "block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5";
const sectionTitleClass = "flex items-center justify-between w-full py-3 text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300 transition-colors";

const MAX_SAMPLE_PHOTOS = 6;

const ManagePackageModal: React.FC<ManagePackageModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  loading = false,
}) => {
  const [expandedSections, setExpandedSections] = useState({
    shoot: true,
    deliverables: true,
    extras: false,
    booking: false,
    samples: false,
  });
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [portfolioItems, setPortfolioItems] = useState<Portfolio[]>([]);
  const [portfolioFolders, setPortfolioFolders] = useState<PortfolioFolder[]>([]);
  const [showPortfolioPicker, setShowPortfolioPicker] = useState(false);
  const [loadingPortfolio, setLoadingPortfolio] = useState(false);
  const MAX_SAMPLE_FOLDERS = 3;

  const toggleSection = (key: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toArrayFields = (arr?: string[]): ArrayField[] =>
    arr && arr.length > 0 ? arr.map(v => ({ value: v })) : [{ value: '' }];

  const { register, control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      title: '', description: '', price: 0, currency: 'INR', category: '',
      session_duration_hours: 2, number_of_days: 1, number_of_looks: 1,
      shoot_type: 'outdoor', team_size: 1,
      locations: [{ value: '' }], travel_included: false, max_travel_km: 0,
      delivery_time_days: 7, number_of_edited_photos: 25, number_of_raw_photos: 0,
      video_included: false, video_duration_seconds: 0, delivery_format: 'google_drive', revisions: 2,
      inclusions: [{ value: '' }], perks: [{ value: '' }], exclusions: [{ value: '' }],
      advance_percentage: 20, requires_consultation: false,
    }
  });

  const incFields = useFieldArray({ control, name: "inclusions" });
  const perkFields = useFieldArray({ control, name: "perks" });
  const excFields = useFieldArray({ control, name: "exclusions" });
  const locFields = useFieldArray({ control, name: "locations" });

  const travelIncluded = watch('travel_included');
  const videoIncluded = watch('video_included');

  useEffect(() => {
    if (initialData) {
      reset({
        title: initialData.title, description: initialData.description,
        price: initialData.price, currency: initialData.currency || 'INR', category: initialData.category,
        session_duration_hours: initialData.session_duration_hours || 2,
        number_of_days: initialData.number_of_days || 1,
        number_of_looks: initialData.number_of_looks || 1,
        shoot_type: initialData.shoot_type || 'outdoor',
        team_size: initialData.team_size || 1,
        locations: toArrayFields(initialData.locations),
        travel_included: initialData.travel_included || false,
        max_travel_km: initialData.max_travel_km || 0,
        delivery_time_days: initialData.delivery_time_days || 7,
        number_of_edited_photos: initialData.number_of_edited_photos || 0,
        number_of_raw_photos: initialData.number_of_raw_photos || 0,
        video_included: initialData.video_included || false,
        video_duration_seconds: initialData.video_duration_seconds || 0,
        delivery_format: initialData.delivery_format || 'google_drive',
        revisions: initialData.revisions || 0,
        inclusions: toArrayFields(initialData.inclusions),
        perks: toArrayFields(initialData.perks),
        exclusions: toArrayFields(initialData.exclusions),
        advance_percentage: initialData.advance_percentage || 20,
        requires_consultation: initialData.requires_consultation || false,
      });
      setSelectedImages(initialData.sample_images || []);
      setSelectedFolders(initialData.portfolio_folders || []);
    } else {
      reset({
        title: '', description: '', price: 0, currency: 'INR', category: '',
        session_duration_hours: 2, number_of_days: 1, number_of_looks: 1,
        shoot_type: 'outdoor', team_size: 1,
        locations: [{ value: '' }], travel_included: false, max_travel_km: 0,
        delivery_time_days: 7, number_of_edited_photos: 25, number_of_raw_photos: 0,
        video_included: false, video_duration_seconds: 0, delivery_format: 'google_drive', revisions: 2,
        inclusions: [{ value: '' }], perks: [{ value: '' }], exclusions: [{ value: '' }],
        advance_percentage: 20, requires_consultation: false,
      });
      setSelectedImages([]);
      setSelectedFolders([]);
    }
  }, [initialData, reset, isOpen]);

  const fetchPortfolio = async () => {
    if (portfolioItems.length > 0) {
      setShowPortfolioPicker(true);
      return;
    }
    setLoadingPortfolio(true);
    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const [itemsRes, foldersRes] = await Promise.all([
        userAPI.getUserPortfolio(currentUser.id, 1, 50),
        userAPI.getOwnPortfolioFolders()
      ]);
      const items = itemsRes.data?.data?.portfolio || itemsRes.data?.data || [];
      const folders = foldersRes.data?.data || foldersRes.data || [];
      setPortfolioItems(items);
      setPortfolioFolders(folders);
      setShowPortfolioPicker(true);
    } catch (err) {
      toast.error('Failed to load portfolio details');
    } finally {
      setLoadingPortfolio(false);
    }
  };

  const toggleImageSelect = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(u => u !== imageUrl);
      }
      if (prev.length >= MAX_SAMPLE_PHOTOS) {
        toast.error(`Maximum ${MAX_SAMPLE_PHOTOS} photos allowed`);
        return prev;
      }
      return [...prev, imageUrl];
    });
  };

  const removeSelectedImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const toggleFolderSelect = (folderId: string) => {
    setSelectedFolders(prev => {
      if (prev.includes(folderId)) {
        return prev.filter(f => f !== folderId);
      }
      if (prev.length >= MAX_SAMPLE_FOLDERS) {
        toast.error(`Maximum ${MAX_SAMPLE_FOLDERS} folders allowed`);
        return prev;
      }
      return [...prev, folderId];
    });
  };

  const removeSelectedFolder = (folderId: string) => {
    setSelectedFolders(prev => prev.filter(f => f !== folderId));
  };

  const extractStrings = (items: ArrayField[]) => items.map(i => i.value).filter(v => v.trim() !== '');

  const onFormSubmit = (data: FormValues) => {
    const formattedData: CreateServicePackageRequest = {
      title: data.title,
      description: data.description,
      price: Number(data.price),
      currency: data.currency,
      category: data.category,
      session_duration_hours: Number(data.session_duration_hours),
      number_of_days: Number(data.number_of_days),
      number_of_looks: Number(data.number_of_looks),
      shoot_type: data.shoot_type,
      team_size: Number(data.team_size),
      locations: extractStrings(data.locations),
      travel_included: data.travel_included,
      max_travel_km: data.travel_included ? Number(data.max_travel_km) : 0,
      delivery_time_days: Number(data.delivery_time_days),
      number_of_edited_photos: Number(data.number_of_edited_photos),
      number_of_raw_photos: Number(data.number_of_raw_photos),
      video_included: data.video_included,
      video_duration_seconds: data.video_included ? Number(data.video_duration_seconds) : 0,
      delivery_format: data.delivery_format,
      revisions: Number(data.revisions),
      inclusions: extractStrings(data.inclusions),
      perks: extractStrings(data.perks),
      exclusions: extractStrings(data.exclusions),
      advance_percentage: Number(data.advance_percentage),
      requires_consultation: data.requires_consultation,
      sample_images: selectedImages,
      portfolio_folders: selectedFolders,
    };
    onSubmit(formattedData);
  };

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot || !isOpen) return null;

  const renderArraySection = (
    title: string, fieldArray: any, name: string, placeholder: string
  ) => (
    <div>
      <label className={labelClass}>{title}</label>
      <div className="space-y-2">
        {fieldArray.fields.map((field: any, index: number) => (
          <div key={field.id} className="flex gap-2">
            <input
              {...register(`${name}.${index}.value` as any)}
              className={inputClass + " flex-grow"}
              placeholder={placeholder}
            />
            {fieldArray.fields.length > 1 && (
              <button type="button" onClick={() => fieldArray.remove(index)}
                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex-shrink-0">
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>
      <button type="button" onClick={() => fieldArray.append({ value: '' })}
        className="mt-2 text-sm text-orange-600 dark:text-orange-400 font-medium flex items-center hover:text-orange-700 dark:hover:text-orange-300">
        <PlusIcon className="h-4 w-4 mr-1" /> Add Item
      </button>
    </div>
  );

  const SectionHeader = ({ title, sectionKey }: { title: string; sectionKey: keyof typeof expandedSections }) => (
    <button type="button" onClick={() => toggleSection(sectionKey)} className={sectionTitleClass}>
      <span>{title}</span>
      {expandedSections[sectionKey] ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
    </button>
  );

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-100 dark:border-gray-800 flex flex-col">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {initialData ? 'Edit Service Package' : 'Create New Package'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Scrollable Form */}
        <form onSubmit={handleSubmit(onFormSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">

            {/* ========== BASIC INFO ========== */}
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Package Title *</label>
                <input {...register('title', { required: 'Title is required' })} className={inputClass}
                  placeholder="e.g., Gold Wedding Package" />
                {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
              </div>

              <div>
                <label className={labelClass}>Description *</label>
                <textarea {...register('description', { required: 'Description is required' })} className={inputClass}
                  rows={3} placeholder="Describe what this package offers..." />
                {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Price (₹) *</label>
                  <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('price', { required: 'Price is required', min: 0 })} className={inputClass} />
                  {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price.message}</p>}
                </div>
                <div>
                  <label className={labelClass}>Category *</label>
                  <select {...register('category', { required: 'Category is required' })} className={inputClass}>
                    <option value="">Select Category</option>
                    <option value="Wedding">Wedding</option>
                    <option value="Pre-Wedding">Pre-Wedding</option>
                    <option value="Event">Event</option>
                    <option value="Portrait">Portrait</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Fashion">Fashion</option>
                    <option value="Product">Product</option>
                    <option value="Maternity">Maternity</option>
                    <option value="Baby">Baby / Newborn</option>
                    <option value="Birthday">Birthday</option>
                    <option value="Corporate">Corporate</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category.message}</p>}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ========== SHOOT DETAILS + LOCATIONS ========== */}
            <div>
              <SectionHeader title="Shoot Details & Locations" sectionKey="shoot" />
              {expandedSections.shoot && (
                <div className="space-y-5 pt-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Duration (hrs)</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} step="0.5" {...register('session_duration_hours', { min: 0.5 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Number of Days</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('number_of_days', { min: 1 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Number of Looks</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('number_of_looks', { min: 1 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Shoot Type</label>
                      <select {...register('shoot_type')} className={inputClass}>
                        <option value="outdoor">Outdoor</option>
                        <option value="indoor">Indoor</option>
                        <option value="studio">Studio</option>
                        <option value="mixed">Mixed</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Team Size</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('team_size', { min: 1 })} className={inputClass} />
                    </div>
                  </div>

                  {/* Locations */}
                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                    <label className={labelClass}>📍 Shoot Locations</label>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Add cities or specific venues where you shoot</p>
                    <div className="space-y-2">
                      {locFields.fields.map((field: any, index: number) => (
                        <div key={field.id} className="flex gap-2">
                          <input
                            {...register(`locations.${index}.value` as any)}
                            className={inputClass + " flex-grow"}
                            placeholder={index === 0 ? "e.g., Mumbai Marine Drive" : "e.g., Bandra Fort, Juhu Beach"}
                          />
                          {locFields.fields.length > 1 && (
                            <button type="button" onClick={() => locFields.remove(index)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex-shrink-0">
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={() => locFields.append({ value: '' })}
                      className="mt-2 text-sm text-orange-600 dark:text-orange-400 font-medium flex items-center hover:text-orange-700 dark:hover:text-orange-300">
                      <PlusIcon className="h-4 w-4 mr-1" /> Add Location
                    </button>
                  </div>

                  {/* Travel toggle */}
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" {...register('travel_included')} className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">I travel to client's location</span>
                  </div>
                  
                  {travelIncluded && (
                    <div className="w-1/2">
                      <label className={labelClass}>Max Travel Distance (km)</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('max_travel_km', { min: 0 })} className={inputClass} placeholder="50" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ========== DELIVERABLES ========== */}
            <div>
              <SectionHeader title="Deliverables" sectionKey="deliverables" />
              {expandedSections.deliverables && (
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className={labelClass}>Edited Photos</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('number_of_edited_photos', { min: 0 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Raw Photos</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('number_of_raw_photos', { min: 0 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Delivery (Days)</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('delivery_time_days', { required: true, min: 1 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Revisions</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('revisions', { min: 0 })} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Delivery Format</label>
                      <select {...register('delivery_format')} className={inputClass}>
                        <option value="google_drive">Google Drive</option>
                        <option value="wetransfer">WeTransfer</option>
                        <option value="usb">USB Drive</option>
                        <option value="dropbox">Dropbox</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" {...register('video_included')} className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Video / Reel Included</span>
                  </div>

                  {videoIncluded && (
                    <div className="w-1/2">
                      <label className={labelClass}>Video Duration (seconds)</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('video_duration_seconds', { min: 0 })} className={inputClass} placeholder="60" />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ========== PERKS & EXTRAS ========== */}
            <div>
              <SectionHeader title="Inclusions, Perks & Exclusions" sectionKey="extras" />
              {expandedSections.extras && (
                <div className="space-y-5 pt-2">
                  {renderArraySection('What\'s Included', incFields, 'inclusions', 'e.g., Drone Shoot, 50 Edited Photos')}
                  {renderArraySection('Perks', perkFields, 'perks', 'e.g., Free retouching, Same-day previews')}
                  {renderArraySection('Exclusions (Not Included)', excFields, 'exclusions', 'e.g., Travel beyond 10km, Props')}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ========== SAMPLE PHOTOS FROM PORTFOLIO ========== */}
            <div>
              <SectionHeader title={`Sample Work (Photos & Folders)`} sectionKey="samples" />
              {expandedSections.samples && (
                <div className="pt-2">
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                    Select photos or link entire portfolio folders to showcase this package
                  </p>
                  {/* Selected items preview */}
                  {(selectedImages.length > 0 || selectedFolders.length > 0) && (
                    <div className="mb-4">
                      {selectedFolders.length > 0 && (
                        <div className="mb-3 space-y-2">
                          <h5 className="text-xs font-semibold text-gray-500 uppercase">Selected Folders</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {selectedFolders.map(folderId => {
                              const folderObj = portfolioFolders.find(f => f.id === folderId);
                              return (
                                <div key={folderId} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 border border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                                  <div className="flex items-center gap-2 overflow-hidden pr-2">
                                    <div className="flex bg-gray-200 dark:bg-gray-700 shrink-0 h-6 w-6 items-center justify-center rounded">
                                      <span className="text-xs">📁</span>
                                    </div>
                                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{folderObj ? folderObj.name : 'Unknown Folder'}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeSelectedFolder(folderId)}
                                    className="p-1 hover:bg-red-100 text-red-500 rounded-md transition-colors"
                                  >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {selectedImages.length > 0 && (
                        <div>
                          <h5 className="text-xs font-semibold text-gray-500 uppercase mb-2">Selected Photos</h5>
                          <div className="grid grid-cols-3 gap-3">
                            {selectedImages.map((url, index) => {
                              // If this URL/path is from the initial data, check if we have a signed version for preview
                              const initialIndex = initialData?.sample_images?.indexOf(url);
                              
                              // Check if we can find this item in currently loaded portfolioItems to get its signed URL
                              const portfolioItem = portfolioItems.find(item => item.image_url === url);
                              const signedUrl = portfolioItem?.thumbnail_image_url_signed || portfolioItem?.medium_image_url_signed || portfolioItem?.image_url_signed;

                              const displayUrl = (initialIndex !== undefined && initialIndex !== -1 && initialData?.sample_images_signed?.[initialIndex]) 
                                ? initialData.sample_images_signed[initialIndex] 
                                : (signedUrl || resolveImageUrl(url));
                                
                              return (
                                <div key={index} className="relative group aspect-square rounded-xl overflow-hidden border-2 border-orange-400">
                                  <img src={displayUrl} alt={`Sample ${index + 1}`} className="w-full h-full object-cover" />
                                <button
                                  type="button"
                                  onClick={() => removeSelectedImage(index)}
                                  className="absolute top-1.5 right-1.5 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                                >
                                  <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                                </div>
                              ); })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Button to open portfolio picker */}
                  {selectedImages.length < MAX_SAMPLE_PHOTOS && (
                    <button
                      type="button"
                      onClick={fetchPortfolio}
                      disabled={loadingPortfolio}
                      className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 hover:border-orange-400 dark:hover:border-orange-500 text-gray-500 dark:text-gray-400 hover:text-orange-500 transition-all flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      {loadingPortfolio ? (
                        <div className="w-5 h-5 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
                      ) : (
                        <>
                          <PhotoIcon className="h-5 w-5" />
                          Choose from Portfolio
                        </>
                      )}
                    </button>
                  )}

                  {/* Portfolio picker grid */}
                  {showPortfolioPicker && (
                    <div className="mt-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">Your Portfolio</h4>
                        <button type="button" onClick={() => setShowPortfolioPicker(false)}
                          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          Done
                        </button>
                      </div>

                      {/* Folders List */}
                      {portfolioFolders.length > 0 && (
                        <div className="mb-4">
                          <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Folders</h5>
                          <div className="grid grid-cols-2 gap-2">
                            {portfolioFolders.map(folder => {
                              const isSelected = selectedFolders.includes(folder.id);
                              return (
                                <button
                                  key={folder.id}
                                  type="button"
                                  onClick={() => toggleFolderSelect(folder.id)}
                                  className={`relative flex items-center gap-2 p-2 rounded-lg border transition-all ${
                                    isSelected 
                                      ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-900/10' 
                                      : 'border-gray-200 dark:border-gray-700 hover:border-orange-300 bg-white dark:bg-gray-800'
                                  }`}
                                >
                                  <div className="flex bg-gray-100 dark:bg-gray-700 shrink-0 h-10 w-10 items-center justify-center rounded-md">
                                    <span className="text-lg">📁</span>
                                  </div>
                                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate pr-6">{folder.name}</span>
                                  
                                  {isSelected && (
                                    <CheckCircleIcon className="h-5 w-5 text-orange-500 absolute right-2" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Images List */}
                      <h5 className="text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wider">Individual Photos</h5>
                      {portfolioItems.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">No portfolio photos found.</p>
                      ) : (
                        <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto pr-1 custom-scrollbar">
                          {portfolioItems.map((item) => {
                            const imgUrl = item.image_url;
                            const isSelected = selectedImages.includes(imgUrl);
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => toggleImageSelect(imgUrl)}
                                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                                  isSelected 
                                    ? 'border-orange-500 ring-2 ring-orange-500/30' 
                                    : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                                }`}
                              >
                                <img 
                                  src={resolveImageUrl(item.thumbnail_image_url_signed || item.thumbnail_image_url || item.image_url)} 
                                  alt={item.title}
                                  className="w-full h-full object-cover"
                                />
                                {isSelected && (
                                  <div className="absolute inset-0 bg-orange-500/20 flex items-center justify-center">
                                    <CheckCircleIcon className="h-6 w-6 text-orange-500 drop-shadow-lg" />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

    <div className="border-t border-gray-100 dark:border-gray-800" />

            {/* ========== BOOKING SETTINGS ========== */}
            <div>
              <SectionHeader title="Booking Settings" sectionKey="booking" />
              {expandedSections.booking && (
                <div className="space-y-4 pt-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Advance Payment (%)</label>
                      <input type="number" min="0" onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }} {...register('advance_percentage', { min: 0, max: 100 })} className={inputClass} />
                      <p className="text-xs text-gray-400 mt-1">% client pays upfront to confirm</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" {...register('requires_consultation')} className="sr-only peer" />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-orange-500"></div>
                    </label>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Requires consultation before booking</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex-shrink-0 sticky bottom-0">
            <Button variant="outline" onClick={onClose} type="button" className="dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={loading}>
              {initialData ? 'Update Package' : 'Create Package'}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    modalRoot
  );
};

export default ManagePackageModal;
